import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Api as ApiIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { AdminLayout } from '../components/AdminLayout';
import apiClient from '../utils/api';

interface ExternalApiKey {
  id: string;
  key_name: string;
  api_key: string;
  description: string | null;
  allowed_ips: string[];
  permissions: string[];
  rate_limit_per_minute: number;
  is_active: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  total_requests: number;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

interface ApiLog {
  id: string;
  endpoint: string;
  method: string;
  status_code: number;
  request_ip: string;
  response_time_ms: number;
  error_message: string | null;
  created_at: string;
}

const ExternalApiKeys: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ExternalApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<ExternalApiKey | null>(null);
  const [newKeyResult, setNewKeyResult] = useState<{ api_key: string; api_secret: string } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info'
  });
  const [expandedLogKey, setExpandedLogKey] = useState<string | null>(null);
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // 新規作成フォーム
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAllowedIps, setFormAllowedIps] = useState('');
  const [formRateLimit, setFormRateLimit] = useState(60);
  const [formExpiresInDays, setFormExpiresInDays] = useState<number | ''>('');

  const loadApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.getExternalApiKeys();
      if (res.success) {
        setApiKeys(res.data);
      }
    } catch (e) {
      console.error('APIキー一覧読み込みエラー:', e);
      setSnackbar({ open: true, message: 'APIキーの読み込みに失敗しました', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleCreate = async () => {
    if (!formName.trim()) {
      setSnackbar({ open: true, message: 'キー名を入力してください', severity: 'error' });
      return;
    }

    try {
      const allowedIps = formAllowedIps
        .split(/[,\n]/)
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);

      const res = await apiClient.createExternalApiKey({
        key_name: formName.trim(),
        description: formDescription.trim() || undefined,
        allowed_ips: allowedIps.length > 0 ? allowedIps : undefined,
        rate_limit_per_minute: formRateLimit,
        expires_in_days: formExpiresInDays || undefined,
      });

      if (res.success) {
        setNewKeyResult({
          api_key: res.data.api_key,
          api_secret: res.data.api_secret,
        });
        setCreateDialogOpen(false);
        resetForm();
        loadApiKeys();
        setSnackbar({ open: true, message: 'APIキーを発行しました', severity: 'success' });
      }
    } catch (e) {
      console.error('APIキー作成エラー:', e);
      setSnackbar({ open: true, message: 'APIキーの作成に失敗しました', severity: 'error' });
    }
  };

  const handleToggleActive = async (key: ExternalApiKey) => {
    try {
      const res = await apiClient.updateExternalApiKey(key.id, {
        is_active: !key.is_active,
      });
      if (res.success) {
        loadApiKeys();
        setSnackbar({
          open: true,
          message: key.is_active ? 'APIキーを無効化しました' : 'APIキーを有効化しました',
          severity: 'success'
        });
      }
    } catch (e) {
      console.error('APIキー更新エラー:', e);
      setSnackbar({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  };

  const handleDelete = async (key: ExternalApiKey) => {
    if (!window.confirm(`APIキー「${key.key_name}」を削除しますか？この操作は取り消せません。`)) return;

    try {
      const res = await apiClient.deleteExternalApiKey(key.id);
      if (res.success) {
        loadApiKeys();
        setSnackbar({ open: true, message: 'APIキーを削除しました', severity: 'success' });
      }
    } catch (e) {
      console.error('APIキー削除エラー:', e);
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    }
  };

  const handleRegenerateSecret = async (key: ExternalApiKey) => {
    if (!window.confirm(`APIキー「${key.key_name}」のシークレットを再生成しますか？\n現在のシークレットは無効になります。`)) return;

    try {
      const res = await apiClient.regenerateExternalApiSecret(key.id);
      if (res.success) {
        setNewKeyResult({
          api_key: res.data.api_key,
          api_secret: res.data.api_secret,
        });
        setSnackbar({ open: true, message: 'シークレットを再生成しました', severity: 'success' });
      }
    } catch (e) {
      console.error('シークレット再生成エラー:', e);
      setSnackbar({ open: true, message: '再生成に失敗しました', severity: 'error' });
    }
  };

  const handleOpenEdit = (key: ExternalApiKey) => {
    setEditingKey(key);
    setFormName(key.key_name);
    setFormDescription(key.description || '');
    setFormAllowedIps(key.allowed_ips.join('\n'));
    setFormRateLimit(key.rate_limit_per_minute);
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingKey) return;

    try {
      const allowedIps = formAllowedIps
        .split(/[,\n]/)
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);

      const res = await apiClient.updateExternalApiKey(editingKey.id, {
        key_name: formName.trim(),
        description: formDescription.trim(),
        allowed_ips: allowedIps,
        rate_limit_per_minute: formRateLimit,
      });
      if (res.success) {
        setEditDialogOpen(false);
        setEditingKey(null);
        resetForm();
        loadApiKeys();
        setSnackbar({ open: true, message: 'APIキーを更新しました', severity: 'success' });
      }
    } catch (e) {
      console.error('APIキー更新エラー:', e);
      setSnackbar({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  };

  const handleToggleLogs = async (keyId: string) => {
    if (expandedLogKey === keyId) {
      setExpandedLogKey(null);
      setLogs([]);
      return;
    }

    setExpandedLogKey(keyId);
    setLogsLoading(true);
    try {
      const res = await apiClient.getExternalApiKeyLogs(keyId, { limit: 20 });
      if (res.success) {
        setLogs(res.data);
      }
    } catch (e) {
      console.error('ログ取得エラー:', e);
    } finally {
      setLogsLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: `${label}をコピーしました`, severity: 'info' });
  };

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormAllowedIps('');
    setFormRateLimit(60);
    setFormExpiresInDays('');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ja-JP');
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ApiIcon /> 外部API連携管理
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              外部サービスへCS対応履歴を提供するためのAPIキーを管理します
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setCreateDialogOpen(true); }}
          >
            APIキー発行
          </Button>
        </Box>

        {/* APIキー一覧 */}
        <Paper sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                <TableCell sx={{ fontWeight: 600 }}>キー名</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>APIキー</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>状態</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>制限</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>最終利用</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>リクエスト数</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">読み込み中...</Typography>
                  </TableCell>
                </TableRow>
              ) : apiKeys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      APIキーがまだ発行されていません
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                apiKeys.map((key) => (
                  <React.Fragment key={key.id}>
                    <TableRow sx={{ '&:hover': { backgroundColor: '#F9FAFB' }, borderBottom: '1px solid #E5E7EB' }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{key.key_name}</Typography>
                        {key.description && (
                          <Typography variant="caption" color="text.secondary">{key.description}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', backgroundColor: '#F3F4F6', px: 1, py: 0.3, borderRadius: 1 }}>
                            {key.api_key.substring(0, 12)}...
                          </Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(key.api_key, 'APIキー')}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={key.is_active ? '有効' : '無効'}
                          size="small"
                          color={key.is_active ? 'success' : 'default'}
                          variant={key.is_active ? 'filled' : 'outlined'}
                        />
                        {key.expires_at && new Date(key.expires_at) < new Date() && (
                          <Chip label="期限切れ" size="small" color="error" sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{key.rate_limit_per_minute}回/分</Typography>
                        {key.allowed_ips.length > 0 && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            IP: {key.allowed_ips.length}件
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDate(key.last_used_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{key.total_requests.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="編集">
                            <IconButton size="small" onClick={() => handleOpenEdit(key)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={key.is_active ? '無効化' : '有効化'}>
                            <Switch
                              size="small"
                              checked={key.is_active}
                              onChange={() => handleToggleActive(key)}
                            />
                          </Tooltip>
                          <Tooltip title="シークレット再生成">
                            <IconButton size="small" onClick={() => handleRegenerateSecret(key)}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="アクセスログ">
                            <IconButton size="small" onClick={() => handleToggleLogs(key.id)}>
                              {expandedLogKey === key.id ? <ExpandLessIcon fontSize="small" /> : <HistoryIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="削除">
                            <IconButton size="small" color="error" onClick={() => handleDelete(key)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {/* アクセスログ展開エリア */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedLogKey === key.id ? '1px solid #E5E7EB' : 'none' }}>
                        <Collapse in={expandedLogKey === key.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: '#F9FAFB' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              直近のアクセスログ
                            </Typography>
                            {logsLoading ? (
                              <Typography variant="caption" color="text.secondary">読み込み中...</Typography>
                            ) : logs.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">アクセスログがありません</Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>日時</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>メソッド</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>エンドポイント</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>ステータス</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>IP</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>応答時間</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {logs.map((log) => (
                                    <TableRow key={log.id}>
                                      <TableCell sx={{ fontSize: '0.7rem' }}>{formatDate(log.created_at)}</TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem' }}>
                                        <Chip label={log.method} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>{log.endpoint}</TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem' }}>
                                        <Chip
                                          label={log.status_code}
                                          size="small"
                                          color={log.status_code < 400 ? 'success' : log.status_code < 500 ? 'warning' : 'error'}
                                          sx={{ fontSize: '0.65rem', height: 18 }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem' }}>{log.request_ip}</TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem' }}>{log.response_time_ms}ms</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* API仕様情報 */}
        <Paper sx={{ mt: 3, p: 3, border: '1px solid #E5E7EB', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>API仕様</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            外部サービスからのアクセスには、以下のヘッダーが必要です。
          </Typography>

          <Box sx={{ backgroundColor: '#1E293B', color: '#E2E8F0', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', mb: 2 }}>
            <Box>X-API-Key: csk_xxxxxxxxxxxxxxxxxxxxxx</Box>
            <Box>X-API-Secret: css_xxxxxxxxxxxxxxxxxxxxxx</Box>
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>エンドポイント一覧</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>メソッド</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>パス</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>説明</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { method: 'GET', path: '/api/external/v1/health', desc: 'ヘルスチェック（認証不要）' },
                { method: 'GET', path: '/api/external/v1/chat-rooms', desc: 'チャットルーム一覧取得' },
                { method: 'GET', path: '/api/external/v1/chat-rooms/:id', desc: 'チャットルーム詳細取得' },
                { method: 'GET', path: '/api/external/v1/chat-rooms/:id/messages', desc: 'メッセージ一覧取得（対応履歴）' },
                { method: 'GET', path: '/api/external/v1/chat-rooms/:id/activity-logs', desc: '対応ログ取得（入室・退室記録）' },
                { method: 'GET', path: '/api/external/v1/stats', desc: '統計情報取得' },
              ].map((ep) => (
                <TableRow key={ep.path}>
                  <TableCell>
                    <Chip label={ep.method} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{ep.path}</TableCell>
                  <TableCell>{ep.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {/* 新規作成ダイアログ */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>APIキー発行</DialogTitle>
          <DialogContent>
            <TextField
              label="キー名"
              fullWidth
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="例: 外部CRM連携"
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              label="説明"
              fullWidth
              multiline
              rows={2}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="利用目的を記述"
              sx={{ mb: 2 }}
            />
            <TextField
              label="許可IPアドレス（改行区切り・空欄で全IP許可）"
              fullWidth
              multiline
              rows={3}
              value={formAllowedIps}
              onChange={(e) => setFormAllowedIps(e.target.value)}
              placeholder="例:&#10;192.168.1.1&#10;10.0.0.0"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="レート制限（回/分）"
                type="number"
                value={formRateLimit}
                onChange={(e) => setFormRateLimit(parseInt(e.target.value) || 60)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="有効期限（日・空欄で無期限）"
                type="number"
                value={formExpiresInDays}
                onChange={(e) => setFormExpiresInDays(e.target.value ? parseInt(e.target.value) : '')}
                sx={{ flex: 1 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>キャンセル</Button>
            <Button variant="contained" onClick={handleCreate}>発行</Button>
          </DialogActions>
        </Dialog>

        {/* 編集ダイアログ */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>APIキー編集</DialogTitle>
          <DialogContent>
            <TextField
              label="キー名"
              fullWidth
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              label="説明"
              fullWidth
              multiline
              rows={2}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="許可IPアドレス（改行区切り・空欄で全IP許可）"
              fullWidth
              multiline
              rows={3}
              value={formAllowedIps}
              onChange={(e) => setFormAllowedIps(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="レート制限（回/分）"
              type="number"
              fullWidth
              value={formRateLimit}
              onChange={(e) => setFormRateLimit(parseInt(e.target.value) || 60)}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
            <Button variant="contained" onClick={handleUpdate}>更新</Button>
          </DialogActions>
        </Dialog>

        {/* 発行結果ダイアログ */}
        <Dialog open={!!newKeyResult} onClose={() => setNewKeyResult(null)} maxWidth="sm" fullWidth>
          <DialogTitle>APIキー情報</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              シークレットは今回限りの表示です。安全な場所に保管してください。
            </Alert>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>APIキー</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                value={newKeyResult?.api_key || ''}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' }
                }}
              />
              <IconButton onClick={() => copyToClipboard(newKeyResult?.api_key || '', 'APIキー')}>
                <CopyIcon />
              </IconButton>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>APIシークレット</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={newKeyResult?.api_secret || ''}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' }
                }}
              />
              <IconButton onClick={() => copyToClipboard(newKeyResult?.api_secret || '', 'APIシークレット')}>
                <CopyIcon />
              </IconButton>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setNewKeyResult(null)}>閉じる</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default ExternalApiKeys;
